/* eslint-disable */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Play, Pause, Volume2, VolumeX, ChevronLeft, ChevronRight, Sparkles, Music, Mic, Layers } from 'lucide-react';

/**
 * CinematicReader — immersive full-screen lesson experience.
 *
 *  • Full-screen black canvas with the lesson's AI background art layered behind a vignette + grain.
 *  • Paragraph-by-paragraph reveal — auto-advance when music is playing, manual nav with arrows otherwise.
 *  • Procedural ambient music generator using the Web Audio API. Mood-driven.
 *    No external audio dependency. Lazy initialised on user gesture.
 *
 *  Props:
 *    - lesson: { title, subtitle, body, cinematic_art_url?, cinematic_mood?, cinematic_soundtrack_url? }
 *    - onClose(): void
 */

const MOOD_PRESETS = {
  calm:   { root: 196.00, fifth: 293.66, third: 246.94, pad: 'sine', bpm: 38, lfo: 0.18 },   // G3 / B3 / D4
  focus:  { root: 220.00, fifth: 329.63, third: 277.18, pad: 'triangle', bpm: 44, lfo: 0.12 }, // A3 / E4 / C#4
  grit:   { root: 146.83, fifth: 220.00, third: 174.61, pad: 'sawtooth', bpm: 52, lfo: 0.35 }, // D3 / A3 / F3
  hope:   { root: 261.63, fifth: 392.00, third: 329.63, pad: 'sine', bpm: 42, lfo: 0.16 },     // C4 / G4 / E4
  shadow: { root: 130.81, fifth: 196.00, third: 155.56, pad: 'triangle', bpm: 36, lfo: 0.22 }, // C3 / G3 / Eb3
  storm:  { root: 110.00, fifth: 164.81, third: 138.59, pad: 'sawtooth', bpm: 60, lfo: 0.4 },  // A2 / E3 / C#3
};

function useAmbientEngine() {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const volumeRef = useRef(0.55);

  const stop = useCallback(() => {
    const ctx = ctxRef.current;
    const nodes = nodesRef.current;
    if (!ctx || !nodes) { setPlaying(false); return; }
    try {
      nodes.oscs.forEach((o) => { try { o.stop(); o.disconnect(); } catch {} });
      nodes.master.gain.cancelScheduledValues(ctx.currentTime);
      nodes.master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6);
    } catch {}
    setPlaying(false);
    nodesRef.current = null;
  }, []);

  const setVolume = useCallback((v) => {
    volumeRef.current = v;
    const ctx = ctxRef.current;
    const nodes = nodesRef.current;
    if (ctx && nodes && !muted) {
      nodes.master.gain.cancelScheduledValues(ctx.currentTime);
      nodes.master.gain.linearRampToValueAtTime(v, ctx.currentTime + 0.4);
    }
  }, [muted]);

  const start = useCallback((moodKey = 'focus', initialVolume = 0.55) => {
    const preset = MOOD_PRESETS[moodKey] || MOOD_PRESETS.focus;
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctxRef.current = new AC();
    }
    const ctx = ctxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    // Stop any existing voices first.
    if (nodesRef.current) stop();

    volumeRef.current = initialVolume;

    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);

    // Gentle low-pass filter for warmth.
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1400;
    filter.Q.value = 0.7;
    filter.connect(master);

    // Reverb-ish: feedback delay chain to fake spatial depth without an impulse buffer.
    const delay = ctx.createDelay();
    delay.delayTime.value = 0.42;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.4;
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(filter);

    const oscs = [];
    const voiceGains = [];

    // Three core voices: root, fifth, third (sub-octave on root).
    const tones = [
      { f: preset.root / 2, gain: 0.18 },
      { f: preset.root,     gain: 0.13 },
      { f: preset.fifth,    gain: 0.09 },
      { f: preset.third,    gain: 0.06 },
    ];

    tones.forEach((t) => {
      const o = ctx.createOscillator();
      o.type = preset.pad;
      o.frequency.value = t.f;
      const g = ctx.createGain();
      g.gain.value = t.gain;
      // Soft LFO on each voice for movement.
      const lfo = ctx.createOscillator();
      lfo.frequency.value = preset.lfo * (0.7 + Math.random() * 0.6);
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = t.gain * 0.45;
      lfo.connect(lfoGain).connect(g.gain);
      lfo.start();
      o.connect(g).connect(filter);
      g.connect(delay);  // wet send for spatial echo
      o.start();
      oscs.push(o, lfo);
      voiceGains.push(g);
    });

    // Soft sub bass swell every ~16 beats.
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = preset.root / 4;
    const subGain = ctx.createGain();
    subGain.gain.value = 0;
    sub.connect(subGain).connect(filter);
    sub.start();
    const beat = 60 / preset.bpm;
    // Schedule swells.
    let t0 = ctx.currentTime + 1;
    for (let i = 0; i < 12; i++) {
      subGain.gain.setValueAtTime(0, t0);
      subGain.gain.linearRampToValueAtTime(0.08, t0 + beat * 4);
      subGain.gain.linearRampToValueAtTime(0, t0 + beat * 12);
      t0 += beat * 16;
    }
    oscs.push(sub);

    // Fade master in.
    master.gain.cancelScheduledValues(ctx.currentTime);
    master.gain.linearRampToValueAtTime(muted ? 0 : initialVolume, ctx.currentTime + 1.8);

    nodesRef.current = { master, oscs, voiceGains };
    setPlaying(true);
  }, [stop, muted]);

  const toggleMute = useCallback(() => {
    const ctx = ctxRef.current;
    const nodes = nodesRef.current;
    setMuted((m) => {
      const nm = !m;
      if (ctx && nodes) {
        nodes.master.gain.cancelScheduledValues(ctx.currentTime);
        nodes.master.gain.linearRampToValueAtTime(nm ? 0 : volumeRef.current, ctx.currentTime + 0.4);
      }
      return nm;
    });
  }, []);

  useEffect(() => () => stop(), [stop]);
  return { playing, muted, start, stop, toggleMute, setVolume };
}

export default function CinematicReader({ lesson, onClose }) {
  const paragraphs = (lesson?.body || '').split('\n\n').map((p) => p.trim()).filter(Boolean);
  const [index, setIndex] = useState(0);
  const [hideUI, setHideUI] = useState(false);
  const { playing: scorePlaying, muted, start, stop, toggleMute, setVolume } = useAmbientEngine();
  const autoRef = useRef(null);
  const hideTimerRef = useRef(null);
  const audioRef = useRef(null);

  const mood = lesson?.cinematic_mood || 'focus';
  const art = lesson?.cinematic_art_url;
  const narrationUrl = lesson?.audio_url || '';
  const hasNarration = !!narrationUrl;

  // Default mode = 'both' if narration exists, else 'score'.
  const [mode, setMode] = useState(hasNarration ? 'both' : 'score');
  const [voicePlaying, setVoicePlaying] = useState(false);

  // Reset mode if narration availability changes.
  useEffect(() => { setMode(hasNarration ? 'both' : 'score'); }, [hasNarration]);

  // Are we "playing" at all?
  const playing = scorePlaying || voicePlaying;

  // Master start/stop respecting the current mode.
  const startAll = useCallback(() => {
    // Start ambient score at full or ducked volume depending on mode.
    if (mode === 'score' || mode === 'both') {
      const targetVol = mode === 'both' ? 0.22 : 0.55;
      start(mood, targetVol);
    }
    // Start narration audio.
    if ((mode === 'voice' || mode === 'both') && audioRef.current && narrationUrl) {
      try {
        audioRef.current.currentTime = audioRef.current.currentTime || 0;
        audioRef.current.play().catch(() => {});
      } catch {}
    }
  }, [mode, mood, narrationUrl, start]);

  const stopAll = useCallback(() => {
    stop();
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
    }
  }, [stop]);

  // When mode changes during playback, smoothly switch sources.
  useEffect(() => {
    // Adjust ambient volume based on mode (only if currently playing).
    if (scorePlaying) {
      if (mode === 'voice') {
        stop();
      } else {
        setVolume(mode === 'both' ? 0.22 : 0.55);
      }
    } else if (playing && (mode === 'score' || mode === 'both')) {
      start(mood, mode === 'both' ? 0.22 : 0.55);
    }
    // Handle narration.
    if (audioRef.current && narrationUrl) {
      if (mode === 'voice' || mode === 'both') {
        if (playing && audioRef.current.paused) {
          audioRef.current.play().catch(() => {});
        }
      } else if (!audioRef.current.paused) {
        audioRef.current.pause();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') setIndex((i) => Math.min(paragraphs.length - 1, i + 1));
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') setIndex((i) => Math.max(0, i - 1));
      else if (e.key === ' ') { e.preventDefault(); playing ? stopAll() : startAll(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [paragraphs.length, playing, startAll, stopAll, onClose]);

  // Auto-advance proportional to paragraph length when music plays AND we're in score-only mode.
  // In voice / both modes, paragraph advance follows audio currentTime.
  useEffect(() => {
    if (autoRef.current) clearTimeout(autoRef.current);
    if (!playing) return;
    if (mode !== 'score') return; // voice/both modes sync via audio onTimeUpdate
    const text = paragraphs[index] || '';
    const seconds = Math.max(6, Math.min(22, Math.round(text.length / 18)));
    autoRef.current = setTimeout(() => {
      setIndex((i) => (i < paragraphs.length - 1 ? i + 1 : i));
    }, seconds * 1000);
    return () => clearTimeout(autoRef.current);
  }, [index, playing, paragraphs, mode]);

  // Auto-hide UI after 3s of no mouse activity.
  const resetHideTimer = useCallback(() => {
    setHideUI(false);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setHideUI(true), 3500);
  }, []);
  useEffect(() => {
    resetHideTimer();
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [resetHideTimer]);

  // Body scroll lock.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  const progress = paragraphs.length > 0 ? ((index + 1) / paragraphs.length) * 100 : 0;
  const currentText = paragraphs[index] || '';

  return (
    <div
      className="fixed inset-0 z-[100] bg-black text-white select-none"
      onMouseMove={resetHideTimer}
      onTouchStart={resetHideTimer}
      data-testid="cinematic-reader"
    >
      {/* Background art */}
      {art ? (
        <img
          src={art}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60 pointer-events-none"
          style={{ filter: 'brightness(0.7) contrast(1.05) saturate(0.9)' }}
        />
      ) : (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(212,164,55,0.25),transparent_55%),radial-gradient(circle_at_70%_75%,rgba(50,50,80,0.3),transparent_55%)] pointer-events-none"/>
      )}
      {/* Vignette + grain */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,0.85)_95%)] pointer-events-none" aria-hidden/>
      <div className="absolute inset-0 cinematic-grain opacity-[0.06] mix-blend-overlay pointer-events-none" aria-hidden/>

      {/* Top bar */}
      <div className={`absolute top-0 left-0 right-0 z-20 px-6 md:px-10 py-5 flex items-center justify-between transition-opacity duration-500 ${hideUI ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} data-testid="cinematic-topbar">
        <div className="flex items-center gap-3">
          <Sparkles size={16} className="text-[color:var(--kc-gold)]"/>
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">Cinematic Read</div>
          <span className="text-white/30">·</span>
          <div className="text-xs uppercase tracking-wider text-white/75 truncate max-w-[40vw]">{lesson?.title}</div>
        </div>
        <div className="flex items-center gap-2">
          {/* Mode selector */}
          <div className="hidden sm:flex items-center gap-0.5 bg-white/5 border border-white/10 rounded-full p-0.5 mr-1" data-testid="cinematic-mode-selector">
            <ModePill active={mode === 'score'} onClick={() => setMode('score')} testid="cinematic-mode-score" title="Synthetic ambient music only">
              <Music size={11}/> Score
            </ModePill>
            {hasNarration && (
              <ModePill active={mode === 'voice'} onClick={() => setMode('voice')} testid="cinematic-mode-voice" title="Mickey's narration only">
                <Mic size={11}/> Voice
              </ModePill>
            )}
            {hasNarration && (
              <ModePill active={mode === 'both'} onClick={() => setMode('both')} testid="cinematic-mode-both" title="Mickey's voice over the score">
                <Layers size={11}/> Both
              </ModePill>
            )}
          </div>

          {!playing ? (
            <button onClick={startAll} className="h-9 px-3 inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-xs font-semibold backdrop-blur-sm" data-testid="cinematic-play">
              <Play size={12}/> {mode === 'score' ? 'Start score' : mode === 'voice' ? 'Start voice' : 'Start'}
            </button>
          ) : (
            <>
              <button onClick={stopAll} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Pause" data-testid="cinematic-pause">
                <Pause size={14}/>
              </button>
              <button onClick={toggleMute} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label={muted ? 'Unmute' : 'Mute'} data-testid="cinematic-mute">
                {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
              </button>
            </>
          )}
          <button onClick={onClose} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Exit cinematic" data-testid="cinematic-close">
            <X size={16}/>
          </button>
        </div>
      </div>

      {/* Hidden narration audio element. Time-driven paragraph advance for voice/both modes. */}
      {hasNarration && (
        <audio
          ref={audioRef}
          src={narrationUrl}
          preload="auto"
          onPlay={() => setVoicePlaying(true)}
          onPause={() => setVoicePlaying(false)}
          onEnded={() => setVoicePlaying(false)}
          onTimeUpdate={(e) => {
            if (mode === 'score') return;
            const a = e.currentTarget;
            const total = a.duration || 0;
            if (!total || !paragraphs.length) return;
            const ratio = a.currentTime / total;
            const target = Math.min(paragraphs.length - 1, Math.floor(ratio * paragraphs.length));
            setIndex((cur) => (target !== cur ? target : cur));
          }}
          data-testid="cinematic-narration-audio"
        />
      )}

      {/* Body — animated paragraph */}
      <div className="absolute inset-0 flex items-center justify-center px-6 md:px-12 pointer-events-none">
        <div className="max-w-3xl w-full text-center">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]/80" data-testid="cinematic-chapter-eyebrow">{lesson?.subtitle || 'A chapter from HEADSPACE'}</div>
          <p
            key={index}
            className="cinematic-paragraph mt-6 font-reader text-2xl md:text-4xl leading-[1.45] text-white whitespace-pre-line"
            data-testid="cinematic-paragraph"
          >
            {currentText}
          </p>
        </div>
      </div>

      {/* Bottom controls */}
      <div className={`absolute bottom-0 left-0 right-0 px-6 md:px-10 py-6 transition-opacity duration-500 ${hideUI ? 'opacity-0' : 'opacity-100'}`} data-testid="cinematic-bottombar">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between text-[10px] tracking-[0.22em] uppercase text-white/55">
            <span>{index + 1} / {paragraphs.length || 1}</span>
            <span>{playing ? (mode === 'score' ? 'Auto · pause to read at your pace' : mode === 'voice' ? "Following Mickey's voice" : 'Voice + score · synced') : 'Tap arrows to advance'}</span>
          </div>
          <div className="mt-3 h-[2px] bg-white/15 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[color:var(--kc-gold)] to-[color:var(--kc-gold-2)] transition-[width] duration-500" style={{ width: `${progress}%` }}/>
          </div>
          <div className="mt-5 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
              className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous"
              data-testid="cinematic-prev"
            >
              <ChevronLeft size={18}/>
            </button>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
              Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/70 mx-1">←</kbd> <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/70 mx-1">→</kbd> or <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/70 mx-1">Space</kbd>
            </div>
            <button
              type="button"
              onClick={() => setIndex((i) => Math.min(paragraphs.length - 1, i + 1))}
              disabled={index >= paragraphs.length - 1}
              className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-[color:var(--kc-gold)] text-black hover:bg-[color:var(--kc-gold-2)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next"
              data-testid="cinematic-next"
            >
              <ChevronRight size={18}/>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .cinematic-grain { background-image: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.15) 0px, transparent 1px); background-size: 3px 3px; }
        .cinematic-paragraph { animation: cin-rise 0.9s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes cin-rise {
          from { opacity: 0; transform: translateY(18px) scale(0.99); filter: blur(4px); }
          to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        kbd { font-family: var(--font-mono, ui-monospace, monospace); font-size: 10px; }
      `}</style>
    </div>
  );
}

function ModePill({ active, onClick, children, testid, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`px-2.5 h-7 inline-flex items-center gap-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors ${
        active
          ? 'bg-[color:var(--kc-gold)] text-black'
          : 'text-white/65 hover:text-white hover:bg-white/10'
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
